import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './InstructorApply.css';

// Step definitions
const STEPS = {
  WELCOME: 'welcome',
  NAME: 'name',
  EMAIL: 'email',
  PHONE: 'phone',
  LOCATION: 'location',
  CV: 'cv',
  INTRO_VIDEO: 'intro_video',
  SECOND_VIDEO: 'second_video',
  REVIEW: 'review',
  DONE: 'done',
};

const isArabic = (text) => /[\u0600-\u06FF]/.test(text);

export default function InstructorApply() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [step, setStep] = useState(STEPS.WELCOME);
  const [data, setData] = useState({
    fullName: '', email: '', phone: '', location: '',
    cvLink: '', introVideoLink: '', secondVideoLink: ''
  });
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState('en'); // detected from user input
  const bottomRef = useRef(null);

  const t = (en, ar) => lang === 'ar' ? ar : en;

  const addBot = (text, delay = 0) => {
    setTimeout(() => {
      setMessages(prev => [...prev, { role: 'bot', text }]);
    }, delay);
  };

  const addUser = (text) => {
    setMessages(prev => [...prev, { role: 'user', text }]);
  };

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Welcome message on mount
  useEffect(() => {
    addBot("👋 Welcome to CodeWave! I'm here to guide you through the Instructor application.\n\nThis will take about 5–10 minutes. You'll need:\n• Your CV (Google Drive link)\n• Two short videos (Google Drive links)\n\nReady to begin? Type **Yes** to start! 🚀");
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateDriveLink = (link) => link.trim().startsWith('https://drive.google.com/');

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    // Detect language
    if (isArabic(text)) setLang('ar');
    else setLang('en');

    addUser(text);
    setInput('');
    setLoading(true);

    setTimeout(() => {
      processStep(text);
      setLoading(false);
    }, 500);
  };

  const processStep = (text) => {
    switch (step) {
      case STEPS.WELCOME: {
        const yes = /yes|يه|ايوه|اه|نعم|ok|okay|start|ابد/i.test(text);
        if (yes) {
          setStep(STEPS.NAME);
          addBot(t(
            "Great! Let's start 🎉\n\n**Step 1 of 7 — Personal Info**\n\nWhat is your **full name**?",
            "رائع! لنبدأ 🎉\n\n**الخطوة 1 من 7 — المعلومات الشخصية**\n\nما هو **اسمك الكامل**؟"
          ));
        } else {
          addBot(t(
            "Type **Yes** whenever you're ready to start! 😊",
            "اكتب **نعم** لما تكون مستعد تبدأ! 😊"
          ));
        }
        break;
      }

      case STEPS.NAME: {
        if (text.length < 3) {
          addBot(t("Please enter your full name (at least 3 characters).", "من فضلك أدخل اسمك الكامل (3 أحرف على الأقل)."));
          return;
        }
        setData(d => ({ ...d, fullName: text }));
        setStep(STEPS.EMAIL);
        addBot(t(
          `Nice to meet you, **${text}**! 👋\n\nWhat is your **email address**?`,
          `يسعدني معرفتك، **${text}**! 👋\n\nما هو **بريدك الإلكتروني**؟`
        ));
        break;
      }

      case STEPS.EMAIL: {
        if (!validateEmail(text)) {
          addBot(t(
            "That doesn't look like a valid email. Please enter a valid email (e.g. name@gmail.com).",
            "هذا لا يبدو بريداً إلكترونياً صحيحاً. أدخل بريداً صحيحاً مثل: name@gmail.com"
          ));
          return;
        }
        setData(d => ({ ...d, email: text }));
        setStep(STEPS.PHONE);
        addBot(t(
          "Perfect! ✅\n\nWhat is your **phone number**? (include country code, e.g. +20 1234567890)",
          "ممتاز! ✅\n\nما هو **رقم هاتفك**؟ (مع كود الدولة، مثلاً: +20 1234567890)"
        ));
        break;
      }

      case STEPS.PHONE: {
        if (text.replace(/[\s\-]/g, '').length < 8) {
          addBot(t("Please enter a valid phone number with country code.", "من فضلك أدخل رقم هاتف صحيح مع كود الدولة."));
          return;
        }
        setData(d => ({ ...d, phone: text }));
        setStep(STEPS.LOCATION);
        addBot(t(
          "Great! 📍\n\nWhat is your **city / location**?",
          "رائع! 📍\n\nما هي **مدينتك / موقعك**؟"
        ));
        break;
      }

      case STEPS.LOCATION: {
        if (text.length < 2) {
          addBot(t("Please enter your city or location.", "من فضلك أدخل مدينتك أو موقعك."));
          return;
        }
        setData(d => ({ ...d, location: text }));
        setStep(STEPS.CV);
        addBot(t(
          "**Step 2 of 7 — CV Upload** 📄\n\nPlease upload your CV to **Google Drive** and paste the link here.\n\n⚠️ Make sure the link is **publicly accessible** (anyone with the link can view).\n\nThe link should start with: `https://drive.google.com/`",
          "**الخطوة 2 من 7 — رفع السيرة الذاتية** 📄\n\nمن فضلك ارفع سيرتك الذاتية على **Google Drive** والصق الرابط هنا.\n\n⚠️ تأكد أن الرابط **متاح للعامة** (أي شخص لديه الرابط يمكنه المشاهدة).\n\nالرابط يجب أن يبدأ بـ: `https://drive.google.com/`"
        ));
        break;
      }

      case STEPS.CV: {
        if (!validateDriveLink(text)) {
          addBot(t(
            "❌ The link must start with `https://drive.google.com/`\n\nPlease re-check your Google Drive link and try again.",
            "❌ الرابط يجب أن يبدأ بـ `https://drive.google.com/`\n\nمن فضلك تحقق من رابط Google Drive وحاول مرة أخرى."
          ));
          return;
        }
        setData(d => ({ ...d, cvLink: text }));
        setStep(STEPS.INTRO_VIDEO);
        addBot(t(
          "**Step 3 of 7 — Introduction Video** 🎬\n\nRecord a short video (**1–3 minutes**) introducing yourself and why you want to be an instructor at CodeWave.\n\n1. Upload it to **Google Drive**\n2. Make sure it's **publicly accessible**\n3. Paste the link here\n\nThe link should start with: `https://drive.google.com/`",
          "**الخطوة 3 من 7 — فيديو التعريف** 🎬\n\nسجّل فيديو قصيراً (**1–3 دقائق**) تعرّف فيه بنفسك ولماذا تريد أن تكون مدرساً في CodeWave.\n\n1. ارفعه على **Google Drive**\n2. تأكد أنه **متاح للعامة**\n3. الصق الرابط هنا\n\nالرابط يجب أن يبدأ بـ: `https://drive.google.com/`"
        ));
        break;
      }

      case STEPS.INTRO_VIDEO: {
        if (!validateDriveLink(text)) {
          addBot(t(
            "❌ The link must start with `https://drive.google.com/`. Please try again.",
            "❌ الرابط يجب أن يبدأ بـ `https://drive.google.com/`. حاول مرة أخرى."
          ));
          return;
        }
        setData(d => ({ ...d, introVideoLink: text }));
        setStep(STEPS.SECOND_VIDEO);
        addBot(t(
          "**Step 4 of 7 — Additional Video** 🎥\n\nNow upload a **second video** — this can be a sample teaching moment, demo lesson, or any relevant content that showcases your skills.\n\n1. Upload to **Google Drive**\n2. Make sure it's **publicly accessible**\n3. Paste the link here",
          "**الخطوة 4 من 7 — فيديو إضافي** 🎥\n\nارفع **فيديو ثانياً** — يمكن أن يكون لحظة تدريس، درس تجريبي، أو أي محتوى يُظهر مهاراتك.\n\n1. ارفعه على **Google Drive**\n2. تأكد أنه **متاح للعامة**\n3. الصق الرابط هنا"
        ));
        break;
      }

      case STEPS.SECOND_VIDEO: {
        if (!validateDriveLink(text)) {
          addBot(t(
            "❌ The link must start with `https://drive.google.com/`. Please try again.",
            "❌ الرابط يجب أن يبدأ بـ `https://drive.google.com/`. حاول مرة أخرى."
          ));
          return;
        }
        const finalData = { ...data, secondVideoLink: text };
        setData(finalData);
        setStep(STEPS.REVIEW);
        addBot(t(
          `**Step 5 of 7 — Review Your Application** ✅\n\nPlease review your information:\n\n👤 **Name:** ${finalData.fullName}\n📧 **Email:** ${finalData.email}\n📱 **Phone:** ${finalData.phone}\n📍 **Location:** ${finalData.location}\n📄 **CV:** ${finalData.cvLink}\n🎬 **Intro Video:** ${finalData.introVideoLink}\n🎥 **Second Video:** ${text}\n\nIs everything correct? Type **Confirm** to submit, or tell me what needs to be changed.`,
          `**الخطوة 5 من 7 — مراجعة طلبك** ✅\n\nمن فضلك راجع معلوماتك:\n\n👤 **الاسم:** ${finalData.fullName}\n📧 **البريد الإلكتروني:** ${finalData.email}\n📱 **الهاتف:** ${finalData.phone}\n📍 **الموقع:** ${finalData.location}\n📄 **السيرة الذاتية:** ${finalData.cvLink}\n🎬 **فيديو التعريف:** ${finalData.introVideoLink}\n🎥 **الفيديو الإضافي:** ${text}\n\nهل كل شيء صحيح؟ اكتب **تأكيد** للإرسال، أو أخبرني ما الذي يحتاج إلى تغيير.`
        ));
        break;
      }

      case STEPS.REVIEW: {
        const confirmed = /confirm|تأكيد|تاكيد|نعم|yes|submit|إرسال/i.test(text);
        if (confirmed) {
          submitApplication();
        } else {
          // Allow corrections
          const lower = text.toLowerCase();
          if (lower.includes('name') || lower.includes('اسم')) {
            setStep(STEPS.NAME);
            addBot(t("Sure! What is your full name?", "بالتأكيد! ما هو اسمك الكامل؟"));
          } else if (lower.includes('email') || lower.includes('بريد')) {
            setStep(STEPS.EMAIL);
            addBot(t("Sure! What is your email address?", "بالتأكيد! ما هو بريدك الإلكتروني؟"));
          } else if (lower.includes('phone') || lower.includes('هاتف')) {
            setStep(STEPS.PHONE);
            addBot(t("Sure! What is your phone number?", "بالتأكيد! ما هو رقم هاتفك؟"));
          } else if (lower.includes('location') || lower.includes('مدينة') || lower.includes('موقع')) {
            setStep(STEPS.LOCATION);
            addBot(t("Sure! What is your city/location?", "بالتأكيد! ما هي مدينتك؟"));
          } else if (lower.includes('cv') || lower.includes('resume') || lower.includes('سيرة')) {
            setStep(STEPS.CV);
            addBot(t("Sure! Please paste your CV Google Drive link.", "بالتأكيد! الصق رابط Google Drive للسيرة الذاتية."));
          } else if (lower.includes('intro') || lower.includes('تعريف')) {
            setStep(STEPS.INTRO_VIDEO);
            addBot(t("Sure! Please paste your intro video Google Drive link.", "بالتأكيد! الصق رابط فيديو التعريف."));
          } else if (lower.includes('second') || lower.includes('إضافي') || lower.includes('ثان')) {
            setStep(STEPS.SECOND_VIDEO);
            addBot(t("Sure! Please paste your second video Google Drive link.", "بالتأكيد! الصق رابط الفيديو الإضافي."));
          } else {
            addBot(t(
              "Type **Confirm** to submit your application, or tell me which field you'd like to change (name, email, phone, location, cv, intro video, second video).",
              "اكتب **تأكيد** لإرسال طلبك، أو أخبرني أي حقل تريد تغييره (الاسم، البريد، الهاتف، الموقع، السيرة، الفيديو)."
            ));
          }
        }
        break;
      }

      default:
        break;
    }
  };

  const submitApplication = async () => {
    setLoading(true);
    addBot(t("Submitting your application... ⏳", "جاري إرسال طلبك... ⏳"));
    try {
      await api.post('/instructor-applications/apply', data);
      setStep(STEPS.DONE);
      addBot(t(
        `🎉 **Application Submitted Successfully!**\n\nThank you, **${data.fullName}**! Your application for the Instructor position has been received.\n\nWe will review your submission and get back to you at **${data.email}** within **5–7 business days**.\n\nGood luck! 🍀`,
        `🎉 **تم إرسال الطلب بنجاح!**\n\nشكراً لك، **${data.fullName}**! تم استلام طلبك لوظيفة المدرب.\n\nسنراجع طلبك وسنرد عليك على **${data.email}** خلال **5–7 أيام عمل**.\n\nبالتوفيق! 🍀`
      ));
    } catch (err) {
      addBot(t(
        "❌ Something went wrong while submitting. Please try again.",
        "❌ حدث خطأ أثناء الإرسال. من فضلك حاول مرة أخرى."
      ));
      setStep(STEPS.REVIEW);
    } finally {
      setLoading(false);
    }
  };

  const STEP_ORDER = [
    STEPS.WELCOME, STEPS.NAME, STEPS.EMAIL, STEPS.PHONE,
    STEPS.LOCATION, STEPS.CV, STEPS.INTRO_VIDEO, STEPS.SECOND_VIDEO,
    STEPS.REVIEW, STEPS.DONE
  ];

  const STEP_LABELS = {
    [STEPS.WELCOME]:      t('Welcome', 'مرحباً'),
    [STEPS.NAME]:         t('Full Name', 'الاسم'),
    [STEPS.EMAIL]:        t('Email', 'البريد'),
    [STEPS.PHONE]:        t('Phone', 'الهاتف'),
    [STEPS.LOCATION]:     t('Location', 'الموقع'),
    [STEPS.CV]:           t('Upload CV', 'السيرة الذاتية'),
    [STEPS.INTRO_VIDEO]:  t('Intro Video', 'فيديو التعريف'),
    [STEPS.SECOND_VIDEO]: t('Demo Video', 'الفيديو الإضافي'),
    [STEPS.REVIEW]:       t('Review', 'المراجعة'),
    [STEPS.DONE]:         t('Done ✓', 'تم ✓'),
  };

  const currentIdx = STEP_ORDER.indexOf(step);
  const progress = Math.round((currentIdx / (STEP_ORDER.length - 1)) * 100);

  const renderMessage = (msg, i) => {
    const formatted = msg.text.split('\n').map((line, j) => {
      // handle `code` and **bold**
      const parts = line.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
      return (
        <p key={j} style={{ margin: '2px 0' }}>
          {parts.map((part, k) => {
            if (part.startsWith('`') && part.endsWith('`'))
              return <code key={k}>{part.slice(1, -1)}</code>;
            if (part.startsWith('**') && part.endsWith('**'))
              return <strong key={k}>{part.slice(2, -2)}</strong>;
            return part;
          })}
        </p>
      );
    });

    return (
      <div key={i} className={`ia-message ia-message--${msg.role}`}>
        {msg.role === 'bot' && <div className="ia-avatar">🤖</div>}
        <div className="ia-bubble">{formatted}</div>
        {msg.role === 'user' && <div className="ia-avatar ia-avatar--user">👤</div>}
      </div>
    );
  };

  return (
    <div className="ia-page">

      {/* ── Left panel ─────────────────────────────── */}
      <aside className="ia-left-panel">
        <div className="ia-panel-hero">
          <h2 className="ia-panel-title">
            Join us as an<br /><span>Instructor</span>
          </h2>
          <p className="ia-panel-desc">
            Shape the next generation of developers. Our AI-guided form makes it easy — just chat your way through the application.
          </p>

          <div className="ia-steps-list">
            {STEP_ORDER.filter(s => s !== STEPS.WELCOME).map((s, i) => {
              const sIdx = STEP_ORDER.indexOf(s);
              const isDone = sIdx < currentIdx;
              const isActive = s === step;
              return (
                <div className="ia-step-item" key={s}>
                  <div className={`ia-step-dot ${isDone ? 'ia-step-dot--done' : isActive ? 'ia-step-dot--active' : 'ia-step-dot--pending'}`}>
                    {isDone ? '✓' : i + 1}
                  </div>
                  <span className={`ia-step-label ${isDone ? 'ia-step-label--done' : isActive ? 'ia-step-label--active' : ''}`}>
                    {STEP_LABELS[s]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <p className="ia-panel-footer">
          Your information is encrypted and secured.<br />
          We'll review your application within 5–7 business days.
        </p>
      </aside>

      {/* ── Right panel (chat) ──────────────────────── */}
      <div className="ia-right-panel">

        {/* Topbar */}
        <div className="ia-topbar">
          <div className="ia-topbar-status">
            <div className="ia-status-dot" />
            <span className="ia-status-text">{t('AI Assistant · Online', 'المساعد الذكي · متصل')}</span>
          </div>

          <div className="ia-progress-wrap">
            <div className="ia-progress-track">
              <div className="ia-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="ia-progress-pct">{progress}%</span>
          </div>

          <div className="ia-lang-toggle">
            <button className={`ia-lang-btn ${lang === 'en' ? 'active' : ''}`} onClick={() => setLang('en')}>EN</button>
            <button className={`ia-lang-btn ${lang === 'ar' ? 'active' : ''}`} onClick={() => setLang('ar')}>AR</button>
          </div>
        </div>

        {/* Messages */}
        <div className="ia-chat">
          {messages.map((msg, i) => renderMessage(msg, i))}
          {loading && (
            <div className="ia-message ia-message--bot">
              <div className="ia-avatar">🤖</div>
              <div className="ia-bubble ia-typing"><span /><span /><span /></div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Done banner */}
        {step === STEPS.DONE && (
          <div className="ia-done-banner">
            🎉 {t('Application submitted! We\'ll be in touch soon.', 'تم إرسال الطلب! سنتواصل معك قريباً.')}
          </div>
        )}

        {/* Input */}
        {step !== STEPS.DONE && (
          <>
            <div className="ia-hint">{t('Press Enter to send · Shift+Enter for new line', 'Enter للإرسال · Shift+Enter لسطر جديد')}</div>
            <div className="ia-input-area">
              <div className="ia-input-wrap">
                <textarea
                  className="ia-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder={lang === 'ar' ? 'اكتب ردك هنا...' : 'Type your response here...'}
                  rows={1}
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                  disabled={loading}
                />
              </div>
              <button className="ia-send-btn" onClick={handleSend} disabled={loading || !input.trim()} title="Send">
                ↑
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
